import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { useCallback, useEffect, useMemo, useState } from "react";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import { Print, QrCode } from "@mui/icons-material";
import { Modal, Switch } from "@mui/material";
import { createSparepart, exportEventsCSV, getSparepartEvents, updateSparepart } from "api/inventorySpareparts";
import CustomReactSelect from "components/CustomReactSelect";
import BaseTable from "components/DataTable/BaseTable";
import HeaderCard from "components/DataTable/HeaderCard";
import ImageUploadField from "components/ImageUploadField";
import ListingImageCol from "components/ListingImageCol";
import Loading from "components/Loading";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import RideSelector from "components/RideSelector";
import ShowQRCodeDialog from "components/showQRcodeDialog";
import SparePartTagSelector from "components/SparePartTagSelector";
import VendorSelector from "components/VendorSelector";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useFilterStore from "store/useFilterStore";
import useGlobalStore from "store/useGlobalStore";
import { formatDateTime, getFrequencyFilters, getStatusFilters, jsonToFormData, removeEmptyFields } from "utils/helper";
import * as yup from "yup";
import { parentRoute } from ".";

const eventsTypes = [
    { value: "price_change", label: "Price Change" },
    { value: "stock_adjustment", label: "Stock Adjustment" },
    { value: "usage", label: "Usage" },
];

const types = getFrequencyFilters(false);
const statusSelection = getStatusFilters(false)

const TABLE_COLUMNS = [
    { id: "type", Header: "Type", accessor: "type", align: "left" },
    { id: "quantity_delta", Header: "Qty Adjustment", accessor: "quantity_delta", align: "left" },
    { id: "new_quantity", Header: "New Qty", accessor: "new_quantity", align: "left" },
    { id: "notes", Header: "Notes/From", accessor: "notes", align: "left" },
    { id: "recorded_by", Header: "Recorded By", accessor: "recorded_by", align: "left" },
    { id: "created_at", Header: "Created At", accessor: "created_at", align: "left" },
];

// Add Yup schema (field names unchanged)
const sparepartSchema = yup.object({
    name: yup.string().trim().required("Name is required").max(100, "Max 100 characters"),
    type: yup
        .mixed()
        .test("required", "Type is required", (v) => v !== undefined && v !== null && v !== ""),
    tag: yup
        .mixed()
        .test("required", "Tag is required", (v) => {
            if (v === null || v === undefined || v === "") return false;
            // accept object { value, label } or string id/name
            if (typeof v === "object") return !!(v.value ?? v.id ?? v.name);
            return String(v).trim().length > 0;
        }),
    stock: yup
        .number()
        .typeError("Stock must be a number")
        .required("Stock is required")
        .min(0, "Stock cannot be negative"),
    minimum_quantity: yup
        .number()
        .typeError("Minimum Quantity must be a number")
        .min(0, "Minimum Quantity cannot be negative")
        .nullable()
        .transform((v, o) => (o === "" || o === null ? null : v)),
    desired_quantity: yup
        .number()
        .typeError("Desired Quantity must be a number")
        .min(0, "Desired Quantity cannot be negative")
        .nullable()
        .transform((v, o) => (o === "" || o === null ? null : v)),
    unit_of_measure: yup.string().max(50, "Max 50 characters").nullable(),
    part_number: yup.string().max(100, "Max 100 characters").nullable(),
    storeroom: yup.string().max(100, "Max 100 characters").nullable(),
    qr_code: yup.string().max(100, "Max 100 characters").nullable(),
    description: yup.string().max(250, "Max 250 characters").nullable()
});

// Helper to map Yup errors to your existing formErrors shape
const mapYupErrors = (yupError) => {
    const errs = {};
    if (yupError?.inner?.length) {
        yupError.inner.forEach((e) => {
            if (e.path && !errs[e.path]) errs[e.path] = e.message;
        });
    } else if (yupError?.path) {
        errs[yupError.path] = yupError.message;
    }
    return errs;
};

// Helper: safely cast numeric fields for validation only
const toNum = (v) => (v === '' || v === null || v === undefined ? undefined : Number(v));
const normalizeForValidation = (data) => ({
    ...data,
    stock: toNum(data.stock),
    minimum_quantity: toNum(data.minimum_quantity),
    desired_quantity: toNum(data.desired_quantity),
});

const AddSpareParts = () => {
    const { getFilters } = useFilterStore();
    const navigate = useNavigate();
    const location = useLocation();
    const { setPrevSelectedRow } = useGlobalStore();
    const item = location.state?.item
    const [formData, setFormData] = useState({
        name: "",
        status: "",
        stock: "",
        tag: "",
        type: "",
        ride_id: "",
        unit_of_measure: "",
        minimum_quantity: "",
        desired_quantity: "",
        unit_cost: "",
        part_number: "",
        storeroom: "",
        qr_code: "",
        description: ""
    });
    const [isLoading, setIsLoading] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [restockModalOpen, setRestockModalOpen] = useState(false);
    const [restockForm, setRestockForm] = useState({});
    const [refreshEvent, setRefreshEvent] = useState(0);
    const [exportLoading, setExportLoading] = useState(false);
    const [params, setParams] = useState({});
    const [showQR, setShowQR] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    const isAdd = useMemo(() =>
        location.pathname.includes(`${parentRoute}/add`),
        [location.pathname]
    );

    useEffect(() => {
        if (isAdd) {
            setFormData({ ...formData, generate_qr: 1 })
            return;
        }

        const loadInitialData = async () => {
            if (item) {
                setFormData({
                    name: item.name || "",
                    status: item.status?.id || "",
                    stock: item.stock ?? 0,
                    tag: item.tag || "",
                    type: item.type?.id || "",
                    ride_id: item.ride_id?.id || "",
                    unit_of_measure: item.unit_of_measure || "",
                    minimum_quantity: item.minimum_quantity || "",
                    desired_quantity: item.desired_quantity || "",
                    part_number: item.part_number || "",
                    storeroom: item.storeroom || "",
                    qr_code: item.qr_code || "",
                    generate_qr: !item.qr_code ? 1 : 0,
                    images: item.images?.map(img => img.url) || [],
                    description: item.description || ""
                });
                setRestockForm({
                    stock: 1,
                })
                setVendors(item.vendors?.map(vendor => ({
                    id: vendor.id,
                    name: vendor.name,
                    part_number: vendor.pivot?.part_number,
                    lead_time_days: vendor.pivot?.lead_time_days,
                    current_unit_cost: vendor.pivot?.current_unit_cost
                })) || []);
            }
        };
        loadInitialData();
    }, [isAdd, item]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setFormErrors({});

        // Use normalized data for Yup (no change to your submit payload)
        const candidate = normalizeForValidation(formData);

        try {
            await sparepartSchema.validate(candidate, { abortEarly: false });
        } catch (yupErr) {
            const errs = mapYupErrors(yupErr);
            console.log("Validation errors:", errs);
            setFormErrors(errs);
            setIsLoading(false);
            return;
        }

        let processedFormData = {
            ...formData,
            vendors: vendors.map((vendor) => ({
                id: vendor.id,
                part_number: vendor.part_number ?? "",
                lead_time_days: vendor.lead_time_days ?? "",
                current_unit_cost: vendor.current_unit_cost ?? "",
            })),
        };

        if (processedFormData.images?.length === 0) {
            processedFormData.images = [];
        }

        try {
            let passData = jsonToFormData(removeEmptyFields(processedFormData));

            if (isAdd) {
                await createSparepart(passData);
                toast.success("Spare part added successfully");
                navigate(parentRoute, { state: location.state });
            } else {

                const res = await updateSparepart(item.id, passData);
                toast.success("Spare part updated successfully");
                if (res?.data?.vendors && JSON.stringify(res.data.vendors) !== JSON.stringify(item.vendors)) {
                    setRefreshEvent(prev => prev + 1);
                }
                if (res?.data?.qr_code) {
                    navigate(location.pathname, { state: { ...location.state, item: { ...item, ...res.data } } });
                }
            }
        } catch (err) {
            toast.error(err?.data?.message || (isAdd ? "Failed to create spare part" : "Failed to update spare part"));
            setFormErrors(err?.data?.errors);
        } finally {
            setIsLoading(false);
        }
    }, [formData, navigate, isAdd, vendors, item]);

    const handleValidatedSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setFormErrors({});
            const candidate = normalizeForValidation(formData);
            try {
                await sparepartSchema.validate(candidate, { abortEarly: false });
            } catch (yupErr) {
                const errs = mapYupErrors(yupErr);
                setFormErrors(errs);
                setIsLoading(false);
                toast.error("Please fix the validation errors before submitting.");
                return;
            }
            // If valid, call your original submit logic
            await handleSubmit(e);
        },
        [formData, handleSubmit]
    );

    const createTableRow = useCallback((item) => ({
        type: <MDTypography component="div" variant="standard" color="text">{item.type?.name}</MDTypography>,
        quantity_delta: (
            <MDTypography
                component="div"
                variant="standard"
                color={
                    item.quantity_delta > 0
                        ? "success"
                        : item.quantity_delta < 0
                            ? "error"
                            : "text"
                }
            >
                {item.quantity_delta > 0
                    ? `+${item.quantity_delta}`
                    : item.quantity_delta < 0
                        ? item.quantity_delta
                        : "-"}
            </MDTypography>
        ),
        new_quantity: <MDTypography component="div" variant="standard" color="text">{item.new_quantity ?? '-'}</MDTypography>,
        // unit_cost: <MDTypography component="div" variant="standard" color="text">{item.unit_cost ?? '-'}</MDTypography>,
        notes: item.vendor ?
            <MDTypography
                component="div"
                variant="standard"
                color="text"
            >
                {item.vendor?.name} (RM{item.unit_cost})
            </MDTypography> :
            item.work_order ?
                <MDTypography
                    component="div"
                    variant="standard"
                    color="primary"
                    sx={{ cursor: 'pointer', }}
                    onClick={() => navigate(`/work-orders/${item.work_order?.id}`, { state: { returnState: location.state, returnPathname: location.pathname } })}
                >
                    WO: {item.work_order?.title}
                </MDTypography> :
                item.images?.length > 0 ?
                    <ListingImageCol imgUrl={item.images?.[0]} name={item.notes?.length > 0 ? item.notes : '-'} /> :
                    <MDTypography component="div" variant="standard" color="text">{item.notes ?? '-'}</MDTypography>,
        recorded_by: <MDTypography component="div" variant="standard" color="text">{item.user?.full_name ?? '-'}</MDTypography>,
        created_at: <MDTypography component="div" variant="standard" color="text">
            {formatDateTime(item.created_at)}
        </MDTypography>,
    }), [navigate]);

    const fetchWithFilters = useCallback(async (baseParams) => {
        const filterParams = {
            ...(params?.type ? { type: params?.type.value } : {}),
            ...(params?.from ? { from: params?.from } : {}),
            ...(params?.to ? { to: params?.to } : {}),
            ...baseParams
        };
        return getSparepartEvents(item.id, filterParams);
    }, [params]);

    const filters = (
        <>
            <MDBox sx={{ flex: 1, minWidth: 130 }}>
                <CustomReactSelect
                    label="Type"
                    name="type"
                    value={params?.type || ''}
                    onChange={(newValue) => setParams({ ...params, type: newValue })}
                    options={eventsTypes}
                    isClearable
                />
            </MDBox>
            <MDBox sx={{ flex: 1, minWidth: 130 }}>
                <MDInput
                    type="date"
                    label="From"
                    InputLabelProps={{ shrink: true }}
                    value={params?.from || ''}
                    onChange={e => setParams({ ...params, from: e.target.value })}
                    fullWidth
                />
            </MDBox>
            <MDBox sx={{ flex: 1, minWidth: 130 }}>
                <MDInput
                    type="date"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    value={params?.to || ''}
                    onChange={e => setParams({ ...params, to: e.target.value })}
                    fullWidth
                />
            </MDBox>
        </>
    );

    const exportButton = <MDButton variant="outlined" color="primary" size="small" loading={exportLoading} onClick={() => handleExport('csv')}>Export CSV</MDButton>

    const handleExport = useCallback(() => {
        setExportLoading(true);
        const record = getFilters(window.location.pathname);
        exportEventsCSV(item.id, item.name, { ...params, ...record.baseTable }).finally(() => setExportLoading(false));
    }, [item]);

    const handleRestockSubmit = async () => {
        try {
            setIsLoading(true);
            let processedFormData = restockForm
            const res = await updateSparepart(item.id, jsonToFormData({ ...processedFormData, stock: parseInt(restockForm?.stock) + parseInt(formData?.stock) }, 'patch'));
            toast.success("Spare part restocked successfully");
            setRestockModalOpen(false);
            navigate(location.pathname, { state: { ...location.state, item: res.data } });
            setRefreshEvent(prev => prev + 1);
        } catch (error) {
            toast.error("Failed to restock spare part");
        } finally {
            setIsLoading(false);
        }
    };

    const SparepartEventsTable = useMemo(() => {
        if (isAdd) return null;
        return (
            <Grid item xs={12} mt={-4}>
                <BaseTable
                    key={refreshEvent}
                    title="Spare Parts History"
                    columns={TABLE_COLUMNS}
                    fetchData={fetchWithFilters}
                    createTableRow={createTableRow}
                    additionalFilters={filters}
                    customButton={exportButton}
                />
            </Grid>
        );
    }, [isAdd, refreshEvent, filters]);

    const printQRCode = () => {
        if (!formData?.qr_code) return;
        const printWindow = window.open('', '_blank', 'width=400,height=400');
        if (printWindow) {
            printWindow.document.write(`
                <html>
                    <head>
                        <title>${item?.name} QR Code</title>
                        <style>
                            body { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; margin: 0; }
                            img { width: 250px; height: 250px; }
                            .label { margin-top: 16px; font-size: 18px; }
                        </style>
                    </head>
                    <body>
                        <img src="${item?.qr_image_url}" alt="QR Code" />
                        <div class="label">${formData?.qr_code}</div>
                        <script>
                            window.onload = function() {
                                window.print();
                                window.onafterprint = function() { window.close(); };
                            };
                        </script>
                    </body>
                </html>
            `);
            printWindow.document.close();
        }
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={4} pb={3}>
                <Grid container spacing={7}>
                    <Grid item xs={12}>
                        <Card>
                            <HeaderCard
                                title={isAdd ? "Create a New Spare Part" : "Edit Spare Part"}
                                showBackButton={true}
                                onBackClick={() => { navigate(parentRoute, { state: location.state }); setPrevSelectedRow(item?.id, parentRoute) }}
                                showButton={!isAdd}
                                buttonText="Restock"
                                onButtonClick={() => setRestockModalOpen(true)}
                            />
                            {isLoading && !isAdd ? <Loading /> :
                                <MDBox pt={3} px={2} pb={2}>
                                    <form noValidate onSubmit={handleValidatedSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 'auto' }}>
                                        <Grid item xs={12}>
                                            <Grid container spacing={3} style={{ flexGrow: 1 }}>
                                                <Grid item xs={12}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Name"
                                                        name="name"
                                                        value={formData?.name || ''}
                                                        onChange={handleChange}
                                                        required
                                                        error={Boolean(formErrors.name)}
                                                        helperText={formErrors.name}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <CustomReactSelect
                                                        label="Type"
                                                        name="type"
                                                        variant="outlined"
                                                        value={types.find(type => type.value === (formData?.type))}
                                                        onChange={(selectedOption) => handleChange({ target: { name: 'type', value: selectedOption?.value } })}
                                                        options={types}
                                                        required
                                                        error={!!formErrors.type}
                                                        helperText={formErrors.type}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Part Number"
                                                        name="part_number"
                                                        value={formData?.part_number || ''}
                                                        onChange={handleChange}
                                                        error={!!formErrors.part_number}
                                                        helperText={formErrors.part_number}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <SparePartTagSelector
                                                        fullWidth
                                                        label="Tag"
                                                        name="tag"
                                                        value={formData?.tag || ''}
                                                        onChange={(selectedOption) => handleChange({ target: { name: 'tag', value: selectedOption } })}
                                                        required
                                                        creatable
                                                        error={!!formErrors.tag}
                                                        helperText={formErrors.tag}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <RideSelector
                                                        fullWidth
                                                        label="Ride"
                                                        name="ride_id"
                                                        value={formData?.ride_id || ''}
                                                        onChange={(selectedOption) => handleChange({ target: { name: 'ride_id', value: selectedOption } })}
                                                        required={false}
                                                    />
                                                </Grid>


                                                <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Stock"
                                                        name="stock"
                                                        type="number"
                                                        inputProps={{ min: 0 }}
                                                        value={formData?.stock ?? 0}
                                                        onChange={handleChange}
                                                        required
                                                        error={!!formErrors.stock}
                                                        helperText={formErrors.stock}
                                                    />
                                                </Grid>


                                                <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Storeroom"
                                                        name="storeroom"
                                                        value={formData?.storeroom || ''}
                                                        onChange={handleChange}
                                                        error={!!formErrors.storeroom}
                                                        helperText={formErrors.storeroom}
                                                    />
                                                </Grid>


                                                <Grid item xs={12}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Description"
                                                        name="description"
                                                        multiline
                                                        minRows={2}
                                                        inputProps={{ maxLength: 250 }}
                                                        value={formData?.description || ''}
                                                        onChange={handleChange}
                                                        error={Boolean(formErrors.description)}
                                                        helperText={formErrors.description}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Minimum Quantity"
                                                        name="minimum_quantity"
                                                        type="number"
                                                        value={formData?.minimum_quantity || ''}
                                                        onChange={handleChange}
                                                        error={!!formErrors.minimum_quantity}
                                                        helperText={formErrors.minimum_quantity}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Desired Quantity"
                                                        name="desired_quantity"
                                                        type="number"
                                                        value={formData?.desired_quantity || ''}
                                                        onChange={handleChange}
                                                        error={!!formErrors.desired_quantity}
                                                        helperText={formErrors.desired_quantity}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Unit of Measure"
                                                        name="unit_of_measure"
                                                        value={formData?.unit_of_measure || ''}
                                                        onChange={handleChange}
                                                        error={!!formErrors.unit_of_measure}
                                                        helperText={formErrors.unit_of_measure}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6} style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <MDBox display="flex" alignItems="center" gap={2}>
                                                        <MDInput
                                                            fullWidth
                                                            label="QR Code"
                                                            name="qr_code"
                                                            value={formData?.qr_code || ''}
                                                            onChange={handleChange}
                                                            error={!!formErrors.qr_code}
                                                            helperText={formErrors.qr_code}
                                                            InputProps={{ readOnly: (formData?.generate_qr || (item?.qr_code && formData?.qr_code)) ? true : false }}
                                                        />
                                                        {formData?.qr_code && <Print style={{ cursor: 'pointer', marginLeft: -5 }} onClick={() => printQRCode()} />}
                                                        {formData?.qr_code && <QrCode style={{ cursor: 'pointer', marginLeft: -5 }} onClick={() => setShowQR(true)} />}
                                                        {!formData?.qr_code && <MDBox display="flex" alignItems="center" >
                                                            <MDTypography variant="caption" color="text">Auto Generate</MDTypography>
                                                            <Switch
                                                                checked={formData?.generate_qr || false}
                                                                onChange={(e) => {
                                                                    const isChecked = e.target.checked;
                                                                    handleChange({ target: { name: 'generate_qr', value: isChecked ? 1 : 0 } });
                                                                }}
                                                            />
                                                        </MDBox>}
                                                    </MDBox>
                                                    <ShowQRCodeDialog open={showQR} onClose={() => setShowQR(false)} qrCode={formData?.qr_code} qrCodeURL={item?.qr_image_url} />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <CustomReactSelect
                                                        label="Status"
                                                        name="status"
                                                        variant="outlined"
                                                        value={statusSelection.find(status => status.value === formData?.status)}
                                                        onChange={(selectedOption) => handleChange({ target: { name: 'status', value: selectedOption?.value } })}
                                                        options={statusSelection}
                                                        error={!!formErrors.status}
                                                        helperText={formErrors.status}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                                        <MDTypography variant="h6">Vendors</MDTypography>
                                                        <MDButton
                                                            variant="outlined"
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => {
                                                                setVendors([...vendors, {
                                                                    id: '',
                                                                    name: '',
                                                                    part_number: '',
                                                                    lead_time_days: '',
                                                                    current_unit_cost: ''
                                                                }]);
                                                            }}
                                                        >
                                                            Add Vendor
                                                        </MDButton>
                                                    </MDBox>
                                                    {vendors.map((vendor, index) => (
                                                        <Card key={index} variant="outlined" sx={{ mb: 2, p: 2 }} >
                                                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                                                <MDTypography variant="subtitle1">Vendor {index + 1}</MDTypography>
                                                                <MDButton
                                                                    variant="text"
                                                                    color="error"
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setVendors(vendors.filter((_, i) => i !== index));
                                                                    }}
                                                                >
                                                                    Remove
                                                                </MDButton>
                                                            </MDBox>
                                                            <Grid container spacing={2}>
                                                                <Grid item xs={12} md={6}>
                                                                    <VendorSelector
                                                                        label="Vendor"
                                                                        value={vendor.id ? { value: vendor.id, label: vendor.name } : null}
                                                                        onChange={(selectedOption) => {
                                                                            const updatedVendors = [...vendors];
                                                                            updatedVendors[index] = {
                                                                                ...updatedVendors[index],
                                                                                id: selectedOption?.value,
                                                                                name: selectedOption?.label
                                                                            };
                                                                            setVendors(updatedVendors);
                                                                        }}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6}>
                                                                    <MDInput
                                                                        fullWidth
                                                                        label="Part Number"
                                                                        value={vendor.part_number || ''}
                                                                        onChange={(e) => {
                                                                            const updatedVendors = [...vendors];
                                                                            updatedVendors[index].part_number = e.target.value;
                                                                            setVendors(updatedVendors);
                                                                        }}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6}>
                                                                    <MDInput
                                                                        fullWidth
                                                                        label="Lead Time (Days)"
                                                                        type="number"
                                                                        value={vendor.lead_time_days || ''}
                                                                        onChange={(e) => {
                                                                            const updatedVendors = [...vendors];
                                                                            updatedVendors[index].lead_time_days = e.target.value;
                                                                            setVendors(updatedVendors);
                                                                        }}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6}>
                                                                    <MDInput
                                                                        fullWidth
                                                                        label="Unit Cost"
                                                                        type="number"
                                                                        step="0.01"
                                                                        value={vendor.current_unit_cost || ''}
                                                                        onChange={(e) => {
                                                                            const updatedVendors = [...vendors];
                                                                            updatedVendors[index].current_unit_cost = e.target.value;
                                                                            setVendors(updatedVendors);
                                                                        }}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </Card>
                                                    ))}
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <ImageUploadField
                                                        value={formData.images ?? ""}
                                                        onChange={(file) => setFormData(prev => ({ ...prev, images: file }))}
                                                        name="images"
                                                        label="Spare Part Image"
                                                        multiple
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    {/* <Grid container>
                                                        <Grid item>
                                                            {!isAdd && <MDButton
                                                                variant="contained"
                                                                color="error"
                                                                onClick={handleDelete}
                                                                loading={isLoading}
                                                            >
                                                                Delete
                                                            </MDButton>}
                                                        </Grid>
                                                    </Grid> */}
                                                    <Grid item xs />
                                                    <Grid item>
                                                        <MDBox display="flex" justifyContent="flex-end">
                                                            <MDButton
                                                                variant="contained"
                                                                color="primary"
                                                                type="submit"
                                                                loading={isLoading}
                                                            >
                                                                Submit
                                                            </MDButton>
                                                        </MDBox>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </form>
                                </MDBox>}

                            <Modal
                                open={restockModalOpen}
                                onClose={() => setRestockModalOpen(false)}
                                aria-labelledby="restock-modal-title"
                                aria-describedby="restock-modal-description"
                            >
                                <MDBox
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        minWidth: 500,
                                        bgcolor: 'background.paper',
                                        boxShadow: 24,
                                        p: 3,
                                        borderRadius: 2,
                                    }}
                                    onClick={e => e.stopPropagation()}
                                >
                                    <MDTypography id="restock-modal-title" variant="h6" mb={3}>Restock Spare Part</MDTypography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <MDInput
                                                label="Stock Adjustment"
                                                value={restockForm.stock}
                                                type="number"
                                                InputProps={{ min: 0, step: 1 }}
                                                onChange={e => setRestockForm(f => ({ ...f, stock: e.target.value }))}
                                                fullWidth
                                            />
                                            <MDBox display="flex" justifyContent="space-between" pt={1}>
                                                <MDTypography variant="subtitle2" fontWeight="regular">Current Stock: {formData.stock}</MDTypography>
                                                <MDTypography variant="subtitle2" fontWeight="regular">New Stock Qty: {parseInt(formData.stock) + parseInt(restockForm.stock ?? 0)}</MDTypography>
                                            </MDBox>
                                        </Grid>
                                        {/* <Grid item xs={12}>
                                            <MDInput
                                                label="Unit Cost (RM)"
                                                type="number"
                                                name="unit_cost"
                                                value={restockForm.unit_cost}
                                                onChange={e => setRestockForm(f => ({ ...f, unit_cost: e.target.value }))}
                                                fullWidth
                                            />
                                        </Grid> */}
                                        <Grid item xs={12}>
                                            <MDInput
                                                label="Adjustment Notes"
                                                name="adjustment_notes"
                                                value={restockForm.notes}
                                                onChange={e => setRestockForm(f => ({ ...f, adjustment_notes: e.target.value }))}
                                                fullWidth
                                                multiline
                                                minRows={2}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <ImageUploadField
                                                label="Adjustment Image"
                                                name="adjustment_images"
                                                value={restockForm.adjustment_images ?? ""}
                                                onChange={file => setRestockForm(prev => ({ ...prev, adjustment_images: file }))}
                                            // multiple
                                            />
                                        </Grid>
                                    </Grid>
                                    <MDBox mt={3} display="flex" justifyContent="flex-end" gap={2}>
                                        <MDButton variant="outlined" color="secondary" onClick={() => setRestockModalOpen(false)}>
                                            Cancel
                                        </MDButton>
                                        <MDButton variant="contained" color="primary" onClick={() => handleRestockSubmit()}
                                            disabled={!restockForm.stock && !restockForm.unit_cost} >
                                            Confirm Restock
                                        </MDButton>
                                    </MDBox>
                                </MDBox>
                            </Modal>
                        </Card>
                    </Grid>
                    {SparepartEventsTable}
                </Grid>
            </MDBox>
        </DashboardLayout >
    );
};

export default AddSpareParts;